<template>
  <div class="editor-root" data-testid="root-editor">
    <input
      v-if="hasInlineImage"
      :id="fileUploadKey"
      :ref="fileUploadKey"
      type="file"
      accept="image/png, image/jpeg, image/gif"
      style="display: none"
      @change="onEditorFileUpload"
    />
    <tag-agents
      v-if="showUserMentions && isPrivate"
      :search-key="mentionSearchKey"
      @click="insertMentionNode"
    />
    <canned-response
      v-if="shouldShowCannedResponses"
      :search-key="cannedSearchTerm"
      @click="insertCannedResponse"
    />
    <variable-list
      v-if="shouldShowVariables"
      :search-key="variableSearchTerm"
      @click="insertVariable"
    />
    <div :ref="fileUploadKey + 'editor'" data-testid="editor" />
  </div>
</template>

<script>
import {
  messageSchema as baseSchema,
  wootMessageWriterSetup,
  EditorView,
  MessageMarkdownTransformer,
  MessageMarkdownSerializer as BaseSerializer,
  EditorState,
  Selection,
} from '@chatwoot/prosemirror-schema';
import {
  suggestionsPlugin,
  triggerCharacters,
} from '@chatwoot/prosemirror-schema/src/mentions/plugin';
import { MarkdownSerializer } from 'prosemirror-markdown';
import { DOMParser as ProseMirrorDOMParser } from 'prosemirror-model';

import TagAgents from '../conversation/TagAgents';
import CannedResponse from '../conversation/CannedResponse';
import VariableList from '../conversation/VariableList';

const TYPING_INDICATOR_IDLE_TIME = 4000;

import {
  hasPressedEnterAndNotCmdOrShift,
  hasPressedCommandAndEnter,
  hasPressedAltAndPKey,
  hasPressedAltAndLKey,
} from 'shared/helpers/KeyboardHelpers';
import eventListenerMixins from 'shared/mixins/eventListenerMixins';
import uiSettingsMixin from 'dashboard/mixins/uiSettings';
import { isEditorHotKeyEnabled } from 'dashboard/mixins/uiSettings';
import { replaceVariablesInMessage } from '@chatwoot/utils';
import { CONVERSATION_EVENTS } from '../../../helper/AnalyticsHelper/events';

import { Schema } from 'prosemirror-model';

// Create a writable copy of Chatwoot's schema
let extendedSchema = baseSchema;

if (!baseSchema.spec.nodes.get('image')) {
  const imageNode = {
    inline: false,
    group: 'block',
    draggable: true,
    attrs: { src: {}, alt: { default: null }, title: { default: null } },
    parseDOM: [
      {
        tag: 'img[src]',
        getAttrs: dom => ({
          src: dom.getAttribute('src'),
          alt: dom.getAttribute('alt'),
          title: dom.getAttribute('title'),
        }),
      },
    ],
    toDOM: node => ['img', node.attrs],
  };

  const extendedNodes = baseSchema.spec.nodes.addBefore(
    'text',
    'image',
    imageNode
  );

  extendedSchema = new Schema({
    nodes: extendedNodes,
    marks: baseSchema.spec.marks,
  });
}

const imageSerializer = {
  ...BaseSerializer.nodes,
  image(state, node) {
    state.write(`![${node.attrs.alt || ''}](${node.attrs.src})`);
  },
};

export const ExtendedMarkdownSerializer = new MarkdownSerializer(
  imageSerializer,
  BaseSerializer.marks
);

const convertMarkdownImages = input =>
  input.replace(/!\[.*?\]\((.*?)\)/g, '<img src="$1" />');

// Fix backslashes and excessive line breaks
const cleanTextFormatting = input =>
  input
    .replace(/\\+/g, '') // remove multiple backslashes
    .replace(/\r?\n/g, '<br>') // convert newlines to <br> tags
    .replace(/(<br>\s*){3,}/g, '<br><br>'); // collapse excessive <br>

const normalizeRawHtml = input => {
  const doc = new DOMParser().parseFromString(input, 'text/html');
  return doc.body;
};

const cleanInput = input =>
  normalizeRawHtml(cleanTextFormatting(convertMarkdownImages(input)));

const createState = (content, placeholder, plugins = []) => {
  const body = cleanInput(content);
  const doc = ProseMirrorDOMParser.fromSchema(extendedSchema).parse(body);

  return EditorState.create({
    doc,
    plugins: wootMessageWriterSetup({
      schema: extendedSchema,
      placeholder,
      plugins,
    }),
  });
};

export default {
  name: 'WootMessageEditor',
  components: { TagAgents, CannedResponse, VariableList },
  mixins: [eventListenerMixins, uiSettingsMixin],
  props: {
    value: { type: String, default: '' },
    editorId: { type: String, default: '' },
    placeholder: { type: String, default: '' },
    isPrivate: { type: Boolean, default: false },
    enableSuggestions: { type: Boolean, default: true },
    overrideLineBreaks: { type: Boolean, default: false },
    hasInlineImage: { type: Boolean, default: false },
    updateSelectionWith: { type: String, default: '' },
    enableVariables: { type: Boolean, default: false },
    enableCannedResponses: { type: Boolean, default: true },
    variables: { type: Object, default: () => ({}) },
    fileUploadKey: { type: String, default: 'inlinefile' },
    onEditorFileUpload: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      showUserMentions: false,
      showCannedMenu: false,
      showVariables: false,
      mentionSearchKey: '',
      cannedSearchTerm: '',
      variableSearchTerm: '',
      editorView: null,
      range: null,
      state: undefined,
    };
  },
  computed: {
    contentFromEditor() {
      return ExtendedMarkdownSerializer.serialize(this.editorView.state.doc);
    },
    shouldShowVariables() {
      return this.enableVariables && this.showVariables && !this.isPrivate;
    },
    shouldShowCannedResponses() {
      return (
        this.enableCannedResponses && this.showCannedMenu && !this.isPrivate
      );
    },
    plugins() {
      if (!this.enableSuggestions) {
        return [];
      }

      return [
        suggestionsPlugin({
          matcher: triggerCharacters('@'),
          onEnter: args => {
            this.showUserMentions = true;
            this.range = args.range;
            this.editorView = args.view;
            return false;
          },
          onChange: args => {
            this.editorView = args.view;
            this.range = args.range;

            this.mentionSearchKey = args.text.replace('@', '');

            return false;
          },
          onExit: () => {
            this.mentionSearchKey = '';
            this.showUserMentions = false;
            return false;
          },
          onKeyDown: ({ event }) => {
            return event.keyCode === 13 && this.showUserMentions;
          },
        }),
        suggestionsPlugin({
          matcher: triggerCharacters('/'),
          suggestionClass: '',
          onEnter: args => {
            if (this.isPrivate) {
              return false;
            }
            this.showCannedMenu = true;
            this.range = args.range;
            this.editorView = args.view;
            return false;
          },
          onChange: args => {
            this.editorView = args.view;
            this.range = args.range;

            this.cannedSearchTerm = args.text.replace('/', '');
            return false;
          },
          onExit: () => {
            this.cannedSearchTerm = '';
            this.showCannedMenu = false;
            return false;
          },
          onKeyDown: ({ event }) => {
            return event.keyCode === 13 && this.showCannedMenu;
          },
        }),
        suggestionsPlugin({
          matcher: triggerCharacters('{{'),
          suggestionClass: '',
          onEnter: args => {
            if (this.isPrivate) {
              return false;
            }
            this.showVariables = true;
            this.range = args.range;
            this.editorView = args.view;
            return false;
          },
          onChange: args => {
            this.editorView = args.view;
            this.range = args.range;

            this.variableSearchTerm = args.text.replace('{{', '');
            return false;
          },
          onExit: () => {
            this.variableSearchTerm = '';
            this.showVariables = false;
            return false;
          },
          onKeyDown: ({ event }) => {
            return event.keyCode === 13 && this.showVariables;
          },
        }),
      ];
    },
  },
  watch: {
    showUserMentions(updatedValue) {
      this.$emit('toggle-user-mention', this.isPrivate && updatedValue);
    },
    showCannedMenu(updatedValue) {
      this.$emit('toggle-canned-menu', !this.isPrivate && updatedValue);
    },
    showVariables(updatedValue) {
      this.$emit('toggle-variables-menu', !this.isPrivate && updatedValue);
    },
    value(newValue = '') {
      if (newValue !== this.contentFromEditor) {
        this.reloadState();
      }
    },
    editorId() {
      this.showCannedMenu = false;
      this.cannedSearchTerm = '';
      this.reloadState();
    },
    isPrivate() {
      this.reloadState();
    },

    updateSelectionWith(newValue, oldValue) {
      if (!this.editorView) {
        return null;
      }
      if (newValue !== oldValue) {
        if (this.updateSelectionWith !== '') {
          const node = this.editorView.state.schema.text(
            this.updateSelectionWith
          );
          const tr = this.editorView.state.tr.replaceSelectionWith(node);
          this.editorView.focus();
          this.state = this.editorView.state.apply(tr);
          this.emitOnChange();
          this.$emit('clear-selection');
        }
      }
      return null;
    },
  },
  created() {
    this.state = createState(this.value, this.placeholder, this.plugins);
  },
  mounted() {
    this.createEditorView();
    this.editorView.updateState(this.state);
    this.focusEditorInputField();
    if (this.hasInlineImage) this.$nextTick(this.addImageButtonToMenu);
  },
  methods: {
    handleImageUpload() {
      this.$refs[this.fileUploadKey].click();
    },
    addImageButtonToMenu() {
      const menubar = this.$el.querySelector('.ProseMirror-menubar');
      if (!menubar) return;
      const alreadyExists = menubar.querySelector(
        '.ProseMirror-icon[title="Insert image"]'
      );
      if (alreadyExists) return;
      const span = document.createElement('span');
      span.className = 'ProseMirror-menuitem';
      const icon = document.createElement('div');
      icon.className = 'ProseMirror-icon';
      icon.title = 'Insert image';
      icon.innerHTML = `<svg width="16" height="16" fill="none" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" class="icon icon--font file-upload"><path d="M6 2a2 2 0 0 0-2 2v5.207a5.48 5.48 0 0 1 1-.185V4a1 1 0 0 1 1-1h4v3.5A1.5 1.5 0 0 0 11.5 8H15v8a1 1 0 0 1-1 1h-3.6a5.507 5.507 0 0 1-.657 1H14a2 2 0 0 0 2-2V7.414a1.5 1.5 0 0 0-.44-1.06l-3.914-3.915A1.5 1.5 0 0 0 10.586 2H6Zm8.793 5H11.5a.5.5 0 0 1-.5-.5V3.207L14.793 7ZM5.5 19a4.5 4.5 0 1 0 0-9a4.5 4.5 0 0 0 0 9Zm2.354-4.854a.5.5 0 1 1-.708.708L6 13.707V16.5a.5.5 0 0 1-1 0v-2.793l-1.146 1.147a.5.5 0 1 1-.708-.707l2-2A.5.5 0 0 1 5.497 12h.006a.498.498 0 0 1 .348.144l.003.003l2 2Z" fill="currentColor" fill-rule="none" stroke="currentColor" stroke-linecap="none" stroke-linejoin="none" stroke-width="0"></path></svg>`;
      icon.addEventListener('click', () => this.handleImageUpload());
      span.appendChild(icon);
      menubar.appendChild(span);
    },
    addImage(url) {
      if (!this.editorView) return;

      const { schema, tr, selection, doc } = this.editorView.state;
      const { image, paragraph } = schema.nodes;
      if (!image || !paragraph) return;

      const imageNode = image.create({ src: url, alt: '', title: '' });
      const emptyParagraph = paragraph.create();
      const insertPos = selection.from;
      const $pos = doc.resolve(insertPos);
      const parent = $pos.parent;

      const isEmptyParagraph =
        parent.type === paragraph && parent.content.size === 0;

      if (isEmptyParagraph) {
        // Case: cursor is in empty <p>, replace whole block
        const from = $pos.start();
        const to = $pos.end();
        tr.replaceWith(from, to, [imageNode, emptyParagraph]);

        const mappedPos = tr.mapping.map(from + imageNode.nodeSize);
        const $resolved = tr.doc.resolve(
          Math.min(mappedPos, tr.doc.content.size - 1)
        );
        tr.setSelection(Selection.near($resolved, 1));
      } else {
        // Case: inserting into content (split text)
        tr.insert(insertPos, imageNode);
        tr.insert(insertPos + imageNode.nodeSize, emptyParagraph);

        const mappedPos = tr.mapping.map(insertPos + imageNode.nodeSize);
        const $resolved = tr.doc.resolve(
          Math.min(mappedPos, tr.doc.content.size - 1)
        );
        tr.setSelection(Selection.near($resolved, 1));
      }

      tr.scrollIntoView();
      this.editorView.dispatch(tr);
      this.state = this.editorView.state;
      this.emitOnChange();
    },
    reloadState() {
      this.state = createState(this.value, this.placeholder, this.plugins);
      this.editorView.updateState(this.state);
      this.focusEditorInputField();
      if (this.hasInlineImage) this.$nextTick(this.addImageButtonToMenu);
    },
    createEditorView() {
      this.editorView = new EditorView(
        this.$refs[this.fileUploadKey + 'editor'],
        {
          state: this.state,
          dispatchTransaction: tx => {
            this.state = this.state.apply(tx);
            this.emitOnChange();
          },
          handleDOMEvents: {
            keyup: () => {
              this.onKeyup();
            },
            keydown: (view, event) => {
              this.onKeydown(event);
            },
            focus: () => {
              this.onFocus();
            },
            blur: () => {
              this.onBlur();
            },
            paste: (view, event) => {
              const data = event.clipboardData.files;
              if (data.length > 0) {
                event.preventDefault();
              }
            },
          },
        }
      );
    },
    isEnterToSendEnabled() {
      return isEditorHotKeyEnabled(this.uiSettings, 'enter');
    },
    isCmdPlusEnterToSendEnabled() {
      return isEditorHotKeyEnabled(this.uiSettings, 'cmd_enter');
    },
    handleKeyEvents(e) {
      if (hasPressedAltAndPKey(e)) {
        this.focusEditorInputField();
      }
      if (hasPressedAltAndLKey(e)) {
        this.focusEditorInputField();
      }
    },
    focusEditorInputField() {
      const { tr } = this.editorView.state;
      const selection = Selection.atEnd(tr.doc);

      this.editorView.dispatch(tr.setSelection(selection));
      this.editorView.focus();
    },
    insertMentionNode(mentionItem) {
      if (!this.editorView) {
        return null;
      }
      const node = this.editorView.state.schema.nodes.mention.create({
        userId: mentionItem.id,
        userFullName: mentionItem.name,
      });

      const tr = this.editorView.state.tr.replaceWith(
        this.range.from,
        this.range.to,
        node
      );
      this.state = this.editorView.state.apply(tr);
      this.emitOnChange();
      this.$track(CONVERSATION_EVENTS.USED_MENTIONS);

      return false;
    },
    insertCannedResponse(cannedItem) {
      const updatedMessage = replaceVariablesInMessage({
        message: cannedItem,
        variables: this.variables,
      });
      if (!this.editorView) {
        return null;
      }

      let from = this.range.from - 1;
      let node = new MessageMarkdownTransformer(extendedSchema).parse(
        updatedMessage
      );

      if (node.textContent === updatedMessage) {
        node = this.editorView.state.schema.text(updatedMessage);
        from = this.range.from;
      }

      const tr = this.editorView.state.tr.replaceWith(
        from,
        this.range.to,
        node
      );

      this.state = this.editorView.state.apply(tr);
      this.emitOnChange();

      tr.scrollIntoView();
      this.$track(CONVERSATION_EVENTS.INSERTED_A_CANNED_RESPONSE);
      return false;
    },
    insertVariable(variable) {
      if (!this.editorView) {
        return null;
      }
      let node = this.editorView.state.schema.text(`{{${variable}}}`);
      const from = this.range.from;

      const tr = this.editorView.state.tr.replaceWith(
        from,
        this.range.to,
        node
      );

      this.state = this.editorView.state.apply(tr);
      this.emitOnChange();

      // The `{{ }}` are added to the message, but the cursor is placed
      // and onExit of suggestionsPlugin is not called. So we need to manually hide
      this.showVariables = false;
      this.$track(CONVERSATION_EVENTS.INSERTED_A_VARIABLE);
      tr.scrollIntoView();
      return false;
    },

    emitOnChange() {
      this.editorView.updateState(this.state);

      this.$emit('input', this.contentFromEditor);
    },

    hideMentions() {
      this.showUserMentions = false;
    },
    resetTyping() {
      this.$emit('typing-off');
      this.idleTimer = null;
    },
    turnOffIdleTimer() {
      if (this.idleTimer) {
        clearTimeout(this.idleTimer);
      }
    },
    handleLineBreakWhenEnterToSendEnabled(event) {
      if (
        hasPressedEnterAndNotCmdOrShift(event) &&
        this.isEnterToSendEnabled() &&
        !this.overrideLineBreaks
      ) {
        event.preventDefault();
      }
    },
    handleLineBreakWhenCmdAndEnterToSendEnabled(event) {
      if (
        hasPressedCommandAndEnter(event) &&
        this.isCmdPlusEnterToSendEnabled() &&
        !this.overrideLineBreaks
      ) {
        event.preventDefault();
      }
    },
    onKeyup() {
      if (!this.idleTimer) {
        this.$emit('typing-on');
      }
      this.turnOffIdleTimer();
      this.idleTimer = setTimeout(
        () => this.resetTyping(),
        TYPING_INDICATOR_IDLE_TIME
      );
    },
    onKeydown(event) {
      if (this.isEnterToSendEnabled()) {
        this.handleLineBreakWhenEnterToSendEnabled(event);
      }
      if (this.isCmdPlusEnterToSendEnabled()) {
        this.handleLineBreakWhenCmdAndEnterToSendEnabled(event);
      }
    },
    onBlur() {
      this.turnOffIdleTimer();
      this.resetTyping();
      this.$emit('blur');
    },
    onFocus() {
      this.$emit('focus');
    },
  },
};
</script>

<style lang="scss">
@import '~@chatwoot/prosemirror-schema/src/styles/base.scss';

.ProseMirror-menubar-wrapper {
  display: flex;
  flex-direction: column;

  .ProseMirror-menubar {
    min-height: var(--space-two) !important;
    margin-left: var(--space-minus-one);
    padding-bottom: 0;
  }

  > .ProseMirror {
    padding: 0;
    word-break: break-word;
  }
}

.ProseMirror img {
  display: block;
  max-width: 100%;
}

.hide-menubar {
  .ProseMirror-menubar {
    display: none;
  }
}

.editor-root {
  width: 100%;
  position: relative;
}

.ProseMirror-woot-style {
  min-height: 8rem;
  max-height: 12rem;
  overflow: auto;
  img {
    max-width: 100%;
    height: auto;
  }
}

.ProseMirror-prompt {
  z-index: var(--z-index-highest);
  background: var(--color-background-light);
  border-radius: var(--border-radius-normal);
  border: 1px solid var(--color-border);
}

.is-private {
  .prosemirror-mention-node {
    font-weight: var(--font-weight-medium);
    background: var(--s-50);
    color: var(--s-900);
    padding: 0 var(--space-smaller);
  }
  .ProseMirror-menubar {
    background: var(--y-50);
  }
}

.editor-wrap {
  margin-bottom: var(--space-normal);
}

.message-editor {
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-normal);
  padding: 0 var(--space-slab);
  margin-bottom: 0;
}

.editor_warning {
  border: 1px solid var(--r-400);
}

.editor-warning__message {
  color: var(--r-400);
  font-weight: var(--font-weight-normal);
  padding: var(--space-smaller) 0 0 0;
}
</style>
